import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/Combobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Repeat, CalendarCheck, X } from "lucide-react";
import { toast } from "sonner";
import { DAYS, WEEKS, t5, tsec } from "@/components/templates/templateUtils";
import { ResultRows } from "@/components/templates/TemplateResultRows";
import { TemplateWorkerView } from "@/components/templates/TemplateWorkerView";

// Snap any picked date to the nearest Friday (day 5) — the roster grid starts
// on Friday, so only Fridays are valid targets.
const toNearestFriday = (ds) => {
  if (!ds) return ds;
  const d = dayjs(ds);
  if (!d.isValid()) return ds;
  const back = (d.day() - 5 + 7) % 7;      // days since the previous Friday
  const fwd = (5 - d.day() + 7) % 7;       // days until the next Friday
  return (back <= fwd ? d.subtract(back, "day") : d.add(fwd, "day")).format("YYYY-MM-DD");
};
import { FragmentRow } from "@/components/templates/TemplateGridRow";

const emptyEdit = { name: null, template_name: "", participant: null, slots: [] };

export default function Templates() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridWeek, setGridWeek] = useState(1);
  const [edit, setEdit] = useState(null);
  const [activeWeek, setActiveWeek] = useState(1);
  const [saving, setSaving] = useState(false);
  const [apply, setApply] = useState(null);
  const [applyForm, setApplyForm] = useState({ week: 1, friday: "" });
  const [applyResults, setApplyResults] = useState(null);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [cellEdit, setCellEdit] = useState(null);
  const [cellSaving, setCellSaving] = useState(false);

  const openCell = (t, off) => {
    const existing = (t.slots || []).filter((s) => Number(s.week) === gridWeek && Number(s.day_offset) === off)
      .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)))
      .map((s) => ({ start_time: t5(s.start_time), end_time: t5(s.end_time), support_worker: s.support_worker || null }));
    setCellEdit({ name: t.name, template_name: t.template_name, participant_name: t.participant_name || t.participant,
      off, day: DAYS.find((d) => d[1] === off)[0],
      slots: existing.length ? existing : [{ start_time: "09:00", end_time: "12:00", support_worker: null }] });
  };
  const patchCell = (i, patch) => setCellEdit((c) => ({ ...c, slots: c.slots.map((s, j) => j === i ? { ...s, ...patch } : s) }));
  const addCellSlot = () => setCellEdit((c) => ({ ...c, slots: [...c.slots, { start_time: "09:00", end_time: "12:00", support_worker: null }] }));
  const removeCellSlot = (i) => setCellEdit((c) => ({ ...c, slots: c.slots.filter((_, j) => j !== i) }));
  const saveCell = async () => {
    setCellSaving(true);
    try {
      const doc = await api.template(cellEdit.name);
      const others = (doc.slots || []).filter((s) => !(Number(s.week) === gridWeek && Number(s.day_offset) === cellEdit.off))
        .map((s) => ({ week: Number(s.week), day_offset: Number(s.day_offset), start_time: tsec(t5(s.start_time)),
          end_time: tsec(t5(s.end_time)), support_worker: s.support_worker || null, worker_name: s.worker_name || wName(s.support_worker) }));
      const cellSlots = cellEdit.slots.filter((s) => s.start_time && s.end_time)
        .map((s) => ({ week: gridWeek, day_offset: cellEdit.off, start_time: tsec(s.start_time), end_time: tsec(s.end_time),
          support_worker: s.support_worker || null, worker_name: wName(s.support_worker) }));
      await api.updateTemplate(cellEdit.name, { template_name: doc.template_name, participant: doc.participant, property: doc.property, slots: [...others, ...cellSlots] });
      toast.success("Template updated");
      const savedName = cellEdit.name;
      setCellEdit(null); await patchRow(savedName);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setCellSaving(false); }
  };

  const sortRows = (arr) => [...arr].sort((a, b) =>
    String(a.participant_name || a.property_name || a.participant || a.property || "").localeCompare(
      String(b.participant_name || b.property_name || b.participant || b.property || ""), undefined, { sensitivity: "base" }));

  const load = () => {
    setLoading(true);
    api.templates()
      .then((r) => setRows(sortRows(r)))
      .catch(() => toast.error("Load failed")).finally(() => setLoading(false));
  };

  // Refresh a single template row in place (keeps scroll position — no full reload).
  const patchRow = async (name) => {
    try {
      const doc = await api.template(name);
      setRows((prev) => sortRows([...prev.filter((r) => r.name !== name), doc]));
    } catch { load(); }
  };
  useEffect(() => { api.participants().then(setParticipants); api.staff().then(setStaff); load(); }, []); // eslint-disable-line

  const pOpts = participants.map((p) => ({ value: p.name, label: p.display_name || p.name }));
  const wOpts = staff.map((s) => ({ value: s.name, label: s.display_name || s.name }));
  const wName = (id) => staff.find((s) => s.name === id)?.display_name || "";
  const cellSlots = (t, off) => (t.slots || []).filter((s) => Number(s.week) === gridWeek && Number(s.day_offset) === off)
    .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));

  const toggleSelect = (name) => setSelected((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const allSelected = rows.length > 0 && rows.every((t) => selected.has(t.name));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((t) => t.name)));

  const openCreate = () => { setEdit({ ...emptyEdit }); setActiveWeek(gridWeek); };
  const openEdit = async (t) => {
    try {
      const doc = await api.template(t.name);
      setEdit({ name: doc.name, template_name: doc.template_name || "", participant: doc.participant || null,
        slots: (doc.slots || []).map((s) => ({ week: Number(s.week) || 1, day_offset: Number(s.day_offset) || 0,
          start_time: t5(s.start_time), end_time: t5(s.end_time), support_worker: s.support_worker || null })) });
      setActiveWeek(gridWeek);
    } catch { toast.error("Could not open template"); }
  };
  const addSlot = (off) => setEdit((e) => ({ ...e, slots: [...e.slots, { week: activeWeek, day_offset: off, start_time: "09:00", end_time: "12:00", support_worker: null }] }));
  const patchSlot = (idx, patch) => setEdit((e) => ({ ...e, slots: e.slots.map((s, i) => i === idx ? { ...s, ...patch } : s) }));
  const removeSlot = (idx) => setEdit((e) => ({ ...e, slots: e.slots.filter((_, i) => i !== idx) }));

  const saveTemplate = async () => {
    if (!edit.template_name || !edit.participant) return toast.error("Name and participant are required");
    for (const s of edit.slots) if (s.support_worker && (!s.start_time || !s.end_time)) return toast.error("Every slot needs a start and end time");
    setSaving(true);
    try {
      const payload = { template_name: edit.template_name, participant: edit.participant,
        slots: edit.slots.map((s) => ({ week: s.week, day_offset: s.day_offset, start_time: tsec(s.start_time), end_time: tsec(s.end_time), support_worker: s.support_worker || null, worker_name: wName(s.support_worker) })) };
      const res = edit.name ? await api.updateTemplate(edit.name, payload) : await api.createTemplate(payload);
      toast.success(edit.name ? "Template saved" : "Template created");
      const savedName = edit.name || res?.name;
      setEdit(null);
      if (savedName) await patchRow(savedName); else load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const removeTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.template_name}"?`)) return;
    try { await api.deleteTemplate(t.name); toast.success("Template deleted"); setRows((prev) => prev.filter((r) => r.name !== t.name)); setSelected((prev) => { const n = new Set(prev); n.delete(t.name); return n; }); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const openApply = (t) => { setApply(t); setApplyForm({ week: gridWeek, friday: "" }); setApplyResults(null); };
  const doApply = async () => {
    if (!applyForm.friday) return toast.error("Pick the target Friday");
    if (dayjs(applyForm.friday).day() !== 5) return toast.error("The target date must be a Friday");
    setApplying(true); setApplyResults(null);
    try {
      const res = await api.applyTemplate(apply.name, Number(applyForm.week), applyForm.friday);
      setApplyResults(res);
      const flagged = (res.results || []).filter((r) => r.status === "assigned_with_issues").length;
      const msg = `Assigned ${res.assigned} shift${res.assigned === 1 ? "" : "s"} (unpublished)` + (res.failures?.length ? ` · ${res.failures.length} not applied` : "") + (res.emailed ? " · report emailed to you" : "");
      flagged ? toast.warning(`${msg} · ${flagged} flagged for review (conflicts) — shown red on the roster`) : toast.success(msg);
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };

  const openBulk = () => { setBulk({ week: gridWeek, friday: "" }); setBulkResults(null); };
  const doBulkApply = async () => {
    if (!bulk.friday) return toast.error("Pick the target Friday");
    if (dayjs(bulk.friday).day() !== 5) return toast.error("The target date must be a Friday");
    setBulkApplying(true); setBulkResults(null);
    try {
      const res = await api.applyTemplatesBulk([...selected], Number(bulk.week), bulk.friday);
      setBulkResults(res);
      const flagged = (res.groups || []).reduce((n, g) => n + (g.results || []).filter((r) => r.status === "assigned_with_issues").length, 0);
      const msg = `Applied ${res.groups.length} template${res.groups.length === 1 ? "" : "s"} · ${res.total_assigned} assigned (unpublished) · ${res.total_failures} not applied` + (res.emailed ? " · report emailed to you" : "");
      flagged ? toast.warning(`${msg} · ${flagged} flagged for review (conflicts) — shown red on the roster`) : toast.success(msg);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk apply failed"); }
    finally { setBulkApplying(false); }
  };

  const slotsFor = (off) => edit.slots.map((s, i) => ({ ...s, _i: i })).filter((s) => s.week === activeWeek && s.day_offset === off);

  const openSilEdit = () => toast.message("Edit SIL house templates by clicking a day cell below, or re-save from the SIL roster view.");
  const pRows = rows.filter((t) => !t.property);
  const silRows = rows.filter((t) => t.property);

  const renderGrid = (list, headerLabel) => {
    const allSel = list.length > 0 && list.every((t) => selected.has(t.name));
    const toggleAllIn = () => setSelected((prev) => {
      const n = new Set(prev);
      if (allSel) list.forEach((t) => n.delete(t.name)); else list.forEach((t) => n.add(t.name));
      return n;
    });
    return (
      <div className="mt-2 border border-slate-200 rounded-lg overflow-auto max-h-[70vh] bg-white" data-testid={`template-grid-${headerLabel.replace(/\s/g, "-").toLowerCase()}`}>
        <div style={{ minWidth: 1100 }}>
          <div className="grid" style={{ gridTemplateColumns: "230px repeat(7, 1fr)" }}>
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 sticky left-0 top-0 z-30">
              {can.has("templates.edit") && <input type="checkbox" checked={allSel} onChange={toggleAllIn} className="h-3.5 w-3.5 accent-[#bf3b4f] cursor-pointer" data-testid={`select-all-${headerLabel.replace(/\s/g, "-").toLowerCase()}`} />}
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{headerLabel}</span>
            </div>
            {DAYS.map(([lb]) => <div key={lb} className="px-2 py-2 bg-slate-50 border-b border-l border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500 text-center sticky top-0 z-20">{lb}</div>)}
            {list.map((t) => (
              <FragmentRow key={t.name} t={t.property ? { ...t, participant_name: t.property_name || t.property } : t}
                DAYS={DAYS} cellSlots={cellSlots} can={can} selected={selected} toggleSelect={toggleSelect}
                openApply={openApply} openEdit={t.property ? openSilEdit : openEdit} removeTemplate={removeTemplate} onCellClick={openCell} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Roster Templates</h1>
          <p className="text-xs text-slate-500 mt-0.5">A 4-week (Fri–Thu) worker grid across all participants. Select the week, then apply participants onto the roster individually or in bulk.</p>
        </div>
        {can.has("templates.edit") && (
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <Button size="sm" variant="outline" className="h-9 text-xs" onClick={openBulk} data-testid="apply-selected-button">
                <CalendarCheck className="h-3.5 w-3.5 mr-1.5" />Apply selected ({selected.size})
              </Button>
            )}
            <Button size="sm" className="h-9 text-xs" style={{ backgroundColor: "#bf3b4f" }} onClick={openCreate} data-testid="new-template-button"><Plus className="h-3.5 w-3.5 mr-1.5" />New Template</Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 mt-4 max-w-md" data-testid="grid-week-tabs">
        {WEEKS.map((w) => (
          <button key={w} type="button" onClick={() => setGridWeek(w)} data-testid={`grid-week-${w}`}
            className={`h-9 flex-1 rounded text-xs font-semibold border ${gridWeek === w ? "bg-[#bf3b4f] text-white border-[#bf3b4f]" : "bg-white text-slate-600 border-slate-200"}`}>Week {w}</button>
        ))}
      </div>

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> :
        rows.length === 0 ? <div className="mt-10 text-center text-sm text-slate-400"><Repeat className="h-8 w-8 mx-auto mb-2 opacity-40" />No templates yet.</div> : (
          <div className="mt-4 space-y-6">
            <div>
              <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Participants <span className="text-slate-400 font-normal">({pRows.length})</span></h2>
              {pRows.length === 0 ? <div className="text-xs text-slate-400 py-2">No participant templates.</div> : renderGrid(pRows, "Participant")}
            </div>
            <div>
              <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">SIL Houses <span className="text-slate-400 font-normal">({silRows.length})</span></h2>
              {silRows.length === 0 ? <div className="text-xs text-slate-400 py-2">No SIL house templates yet — save one from the SIL view on the roster board.</div> : renderGrid(silRows, "SIL House")}
            </div>
            <TemplateWorkerView templates={rows} week={gridWeek} DAYS={DAYS} staffName={wName} />
          </div>
        )}

      {/* Editor */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-3xl" data-testid="template-editor">
          <DialogHeader><DialogTitle className="text-base">{edit?.name ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-medium text-slate-600">Template name *</label><Input value={edit.template_name} onChange={(e) => setEdit((x) => ({ ...x, template_name: e.target.value }))} className="h-9 text-xs mt-1" data-testid="tmpl-name" /></div>
                <div><label className="text-[11px] font-medium text-slate-600">Participant *</label><div className="mt-1"><Combobox testid="tmpl-participant" options={pOpts} value={edit.participant} onChange={(v) => setEdit((x) => ({ ...x, participant: v }))} placeholder="Select participant" /></div></div>
              </div>
              <div className="flex gap-1" data-testid="week-tabs">
                {WEEKS.map((w) => (
                  <button key={w} type="button" onClick={() => setActiveWeek(w)} data-testid={`week-tab-${w}`}
                    className={`h-8 flex-1 rounded text-[11px] font-medium border ${activeWeek === w ? "bg-[#bf3b4f] text-white border-[#bf3b4f]" : "bg-white text-slate-600 border-slate-200"}`}>Week {w}
                    <span className="ml-1 opacity-70">({edit.slots.filter((s) => s.week === w).length})</span>
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                {DAYS.map(([lb, off]) => (
                  <div key={off} className="border border-slate-200 rounded-md p-2" data-testid={`day-${activeWeek}-${off}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-semibold text-slate-700">{lb}</span>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => addSlot(off)} data-testid={`add-slot-${activeWeek}-${off}`}><Plus className="h-3 w-3 mr-0.5" />Slot</Button>
                    </div>
                    {slotsFor(off).length === 0 ? <div className="text-[10px] text-slate-300">No shifts</div> : (
                      <div className="space-y-1.5">
                        {slotsFor(off).map((s) => (
                          <div key={s._i} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-1.5 items-center" data-testid={`slot-${s._i}`}>
                            <Input type="time" value={s.start_time} onChange={(e) => patchSlot(s._i, { start_time: e.target.value })} className="h-8 text-[11px]" data-testid={`slot-start-${s._i}`} />
                            <Input type="time" value={s.end_time} onChange={(e) => patchSlot(s._i, { end_time: e.target.value })} className="h-8 text-[11px]" data-testid={`slot-end-${s._i}`} />
                            <Combobox testid={`slot-worker-${s._i}`} options={wOpts} value={s.support_worker} onChange={(v) => patchSlot(s._i, { support_worker: v })} placeholder="Worker" />
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeSlot(s._i)} data-testid={`remove-slot-${s._i}`}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEdit(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={saveTemplate} disabled={saving} data-testid="tmpl-save-confirm">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (edit?.name ? "Save template" : "Create template")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply single */}
      <Dialog open={!!apply} onOpenChange={(v) => !v && setApply(null)}>
        <DialogContent className="max-w-2xl" data-testid="apply-dialog">
          <DialogHeader><DialogTitle className="text-base">Apply — {apply?.template_name} <span className="text-slate-400 font-normal">· {apply?.participant_name}</span></DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Fills the template week's workers into matching, still-unassigned shifts (SIL house templates match any shift in the house by day + time). Assigned shifts stay <strong>unpublished</strong> for review. Workers with a conflict (expired documents, approved leave, unavailability, double-booking, etc.) are <strong>still assigned but flagged red</strong> on the roster and reported below — a copy is emailed to you.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-medium text-slate-600">Template week</label>
              <select value={applyForm.week} onChange={(e) => { setApplyForm((f) => ({ ...f, week: e.target.value })); setApplyResults(null); }} className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full mt-1" data-testid="apply-week">
                {WEEKS.map((w) => <option key={w} value={w}>Week {w}</option>)}
              </select>
            </div>
            <div><label className="text-[11px] font-medium text-slate-600">Target Friday</label><Input type="date" value={applyForm.friday} onChange={(e) => { const fri = toNearestFriday(e.target.value); if (e.target.value && fri !== e.target.value) toast.message("Snapped to the nearest Friday"); setApplyForm((f) => ({ ...f, friday: fri })); setApplyResults(null); }} className="h-9 text-xs mt-1" data-testid="apply-friday" /></div>
          </div>
          {applyResults && (
            <div className="mt-1 border border-slate-200 rounded-md overflow-hidden" data-testid="apply-results">
              <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 text-[11px]">
                <span className="font-semibold text-slate-700">{applyResults.assigned} assigned · {applyResults.failures?.length || 0} not applied</span>
                <span className="text-slate-400">{applyResults.emailed ? `Report emailed to ${applyResults.email_to}` : "Email not sent (address undeliverable)"}</span>
              </div>
              <div className="max-h-64 overflow-auto"><table className="w-full text-[11px]"><tbody><ResultRows results={applyResults.results || []} prefix="result" /></tbody></table></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setApply(null)}>{applyResults ? "Close" : "Cancel"}</Button>
            <Button size="sm" style={{ backgroundColor: "#0D9488" }} onClick={doApply} disabled={applying} data-testid="apply-confirm">
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (applyResults ? "Apply again" : "Apply to roster")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk apply */}
      <Dialog open={!!bulk} onOpenChange={(v) => !v && setBulk(null)}>
        <DialogContent className="max-w-2xl" data-testid="bulk-apply-dialog">
          <DialogHeader><DialogTitle className="text-base">Apply {selected.size} template{selected.size === 1 ? "" : "s"}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Applies the chosen week of every selected template onto the same target Friday.</p>
          {bulk && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[11px] font-medium text-slate-600">Template week</label>
                <select value={bulk.week} onChange={(e) => { setBulk((f) => ({ ...f, week: e.target.value })); setBulkResults(null); }} className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full mt-1" data-testid="bulk-week">
                  {WEEKS.map((w) => <option key={w} value={w}>Week {w}</option>)}
                </select>
              </div>
              <div><label className="text-[11px] font-medium text-slate-600">Target Friday</label><Input type="date" value={bulk.friday} onChange={(e) => { const fri = toNearestFriday(e.target.value); if (e.target.value && fri !== e.target.value) toast.message("Snapped to the nearest Friday"); setBulk((f) => ({ ...f, friday: fri })); setBulkResults(null); }} className="h-9 text-xs mt-1" data-testid="bulk-friday" /></div>
            </div>
          )}
          {bulkResults && (
            <div className="mt-1 space-y-3 max-h-[55vh] overflow-auto" data-testid="bulk-results">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-semibold text-slate-700">{bulkResults.total_assigned} assigned · {bulkResults.total_failures} not applied across {bulkResults.groups.length} template(s)</span>
                <span className="text-slate-400">{bulkResults.emailed ? `Report emailed to ${bulkResults.email_to}` : "Email not sent (address undeliverable)"}</span>
              </div>
              {bulkResults.groups.map((g, gi) => (
                <div key={gi} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`bulk-group-${gi}`}>
                  <div className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold text-slate-700">{g.template_name} <span className="text-slate-400 font-normal">· {g.participant_name} · {g.assigned} assigned, {g.failures.length} not applied</span></div>
                  <table className="w-full text-[11px]"><tbody><ResultRows results={g.results || []} prefix={`bulk-${gi}`} /></tbody></table>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setBulk(null)}>{bulkResults ? "Close" : "Cancel"}</Button>
            <Button size="sm" style={{ backgroundColor: "#0D9488" }} onClick={doBulkApply} disabled={bulkApplying} data-testid="bulk-apply-confirm">
              {bulkApplying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (bulkResults ? "Apply again" : "Apply all to roster")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick cell edit */}
      <Dialog open={!!cellEdit} onOpenChange={(v) => !v && setCellEdit(null)}>
        <DialogContent className="max-w-lg" data-testid="cell-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {cellEdit?.day} · Week {gridWeek}
              <span className="text-slate-400 font-normal"> · {cellEdit?.participant_name}</span>
            </DialogTitle>
          </DialogHeader>
          {cellEdit && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              <p className="text-xs text-slate-500 -mt-1">Add or edit this day's shift slots for {cellEdit.template_name}. These replace the day's existing slots when saved.</p>
              {cellEdit.slots.length === 0 ? <div className="text-[11px] text-slate-300 py-2">No slots — add one below.</div> : (
                <div className="space-y-1.5">
                  {cellEdit.slots.map((s, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-1.5 items-center" data-testid={`cell-slot-row-${i}`}>
                      <Input type="time" value={s.start_time} onChange={(e) => patchCell(i, { start_time: e.target.value })} className="h-8 text-[11px]" data-testid={`cell-slot-start-${i}`} />
                      <Input type="time" value={s.end_time} onChange={(e) => patchCell(i, { end_time: e.target.value })} className="h-8 text-[11px]" data-testid={`cell-slot-end-${i}`} />
                      <Combobox testid={`cell-slot-worker-${i}`} options={wOpts} value={s.support_worker} onChange={(v) => patchCell(i, { support_worker: v })} placeholder="Worker" />
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeCellSlot(i)} data-testid={`cell-slot-remove-${i}`}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <Button size="sm" variant="outline" className="h-7 text-[10px] px-2" onClick={addCellSlot} data-testid="cell-add-slot"><Plus className="h-3 w-3 mr-0.5" />Add slot</Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCellEdit(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={saveCell} disabled={cellSaving} data-testid="cell-save-confirm">
              {cellSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
